import { LitElement, html, nothing, type TemplateResult, type CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { emit } from '../utils/dom.utils.js';
import styles from './styles.js';

export type ToastPosition =
  | 'top-right' | 'top-left' | 'top-center'
  | 'bottom-right' | 'bottom-left' | 'bottom-center'
  | 'center';

export type ToastSeverity = 'info' | 'success' | 'warn' | 'error';

export interface ToastMessage {
  id?: string | number;
  severity?: ToastSeverity;
  summary?: string;
  detail?: string;
  /** Auto-close in ms. 0 = sticky. Default: inherits component life. */
  life?: number;
  /** Pin the message; never auto-close. */
  sticky?: boolean;
  /** Show close button. Default: true. */
  closable?: boolean;
  /** Override severity icon. */
  icon?: string;
  /** Custom render function replacing the default content. */
  contentTemplate?: (msg: ToastMessage) => TemplateResult;
}

interface _InternalMsg extends Required<Pick<ToastMessage, 'id' | 'severity' | 'closable'>> {
  summary:  string;
  detail:   string;
  life:     number;
  sticky:   boolean;
  icon?:    string;
  contentTemplate?: (msg: ToastMessage) => TemplateResult;
  closing:  boolean;
}

/**
 * uwc-toast
 *
 * Fixed-position notification stack with auto-dismiss and progress bar.
 *
 * @attr position           - top-right | top-left | top-center | bottom-right | bottom-left | bottom-center | center. Default: top-right.
 * @attr life               - Default auto-close duration in ms. 0 = never. Default: 3000.
 * @attr sticky             - Default all messages to never auto-close.
 *
 * @method add(msg)         - Add a ToastMessage (or array).
 * @method clear()          - Remove all messages.
 * @method remove(id)       - Remove a specific message by id.
 *
 * @fires uwc-close         - A message was dismissed. detail: { id, severity, summary }
 * @fires uwc-life-end      - A message's lifetime expired. detail: { id }
 */
export class UwcToast extends LitElement {
  static override styles: CSSResultGroup = styles;

  @property({ reflect: true })
  position: ToastPosition = 'top-right';

  @property({ type: Number })
  life = 3000;

  @property({ type: Boolean })
  sticky = false;

  @state() private _messages: _InternalMsg[] = [];

  private _timers  = new Map<string | number, ReturnType<typeof setTimeout>>();
  private _counter = 0;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._timers.forEach(t => clearTimeout(t));
    this._timers.clear();
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  add(msg: ToastMessage | ToastMessage[]) {
    const list = Array.isArray(msg) ? msg : [msg];
    for (const m of list) {
      const id       = m.id ?? ++this._counter;
      const life     = m.life    ?? this.life;
      const sticky   = m.sticky  ?? this.sticky;
      const closable = m.closable ?? true;

      const internal: _InternalMsg = {
        id,
        severity:        m.severity ?? 'info',
        summary:         m.summary  ?? '',
        detail:          m.detail   ?? '',
        life,
        sticky,
        closable,
        icon:            m.icon,
        contentTemplate: m.contentTemplate,
        closing:         false,
      };

      this._messages = [...this._messages, internal];

      if (!sticky && life > 0) {
        const timer = setTimeout(() => this._expire(id), life);
        this._timers.set(id, timer);
      }
    }
  }

  clear() {
    this._timers.forEach(t => clearTimeout(t));
    this._timers.clear();
    this._messages = [];
  }

  removeMessage(id: string | number) {
    this._startClose(id);
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  private _expire(id: string | number) {
    emit(this, 'uwc-life-end', { id });
    this._startClose(id);
  }

  private _startClose(id: string | number) {
    clearTimeout(this._timers.get(id));
    this._timers.delete(id);
    this._messages = this._messages.map(m =>
      m.id === id ? { ...m, closing: true } : m,
    );
    // Remove from DOM after animation completes
    setTimeout(() => {
      const msg = this._messages.find(m => m.id === id);
      this._messages = this._messages.filter(m => m.id !== id);
      if (msg) emit(this, 'uwc-close', { id: msg.id, severity: msg.severity, summary: msg.summary });
    }, 200);
  }

  private static readonly _ICONS: Record<ToastSeverity, string> = {
    info:    'info-circle-fill',
    success: 'check-circle-fill',
    warn:    'exclamation-triangle-fill',
    error:   'x-circle-fill',
  };

  private _getIcon(m: _InternalMsg) {
    return m.icon ?? UwcToast._ICONS[m.severity] ?? 'info-circle-fill';
  }

  private _renderMessage(m: _InternalMsg) {
    const classes = {
      'uwc-toast-item': true,
      [`uwc-toast-item--${m.severity}`]: true,
      'is-closing': m.closing,
    };

    const progressStyle = (!m.sticky && m.life > 0)
      ? { animationDuration: `${m.life}ms` }
      : {};

    return html`
      <div
        class=${classMap(classes)}
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <div class="uwc-toast-item__inner">
          <span class="uwc-toast-item__icon-wrap" aria-hidden="true">
            <uwc-icon name=${this._getIcon(m)} class="uwc-toast-item__icon"></uwc-icon>
          </span>

          <div class="uwc-toast-item__body">
            ${m.contentTemplate
              ? m.contentTemplate(m)
              : html`
                ${m.summary ? html`<span class="uwc-toast-item__summary">${m.summary}</span>` : nothing}
                ${m.detail  ? html`<span class="uwc-toast-item__detail">${m.detail}</span>`   : nothing}
              `}
          </div>

          ${m.closable ? html`
            <button
              type="button"
              class="uwc-toast-item__close"
              aria-label="Dismiss notification"
              @click=${() => this._startClose(m.id)}
            >
              <uwc-icon name="x-lg"></uwc-icon>
            </button>
          ` : nothing}
        </div>

        ${(!m.sticky && m.life > 0) ? html`
          <div
            class="uwc-toast-item__progress uwc-toast-item__progress--${m.severity}"
            style=${styleMap(progressStyle)}
          ></div>
        ` : nothing}
      </div>
    `;
  }

  override render() {
    return html`
      <div class="uwc-toast-container" role="region" aria-label="Notifications" aria-live="polite">
        ${repeat(
          this._messages,
          m => m.id,
          m => this._renderMessage(m),
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-toast': UwcToast; }
}
